import React, { useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { Box, IconButton, Tab, Tabs, Typography } from "@mui/material"
import Inventory2Icon from "@mui/icons-material/Inventory2"
import UnarchiveIcon from "@mui/icons-material/Unarchive"
import dayjs from "dayjs"

import { getOrganizationsEnterpriseSelector } from "../../../reducers/Organizations/organizations"
import { TableDateFormat } from "../../../components/table-utils/utils"
import { COLORS } from "../../../utils"
import EnterprisesHeader from "./EnterprisesHeader"
import PreviewHeader from "../../../components/PreviewHeader"
import ArchiveEnterpriseConfirmModal from "./ArchiveEnterpriseConfirmModal"
import ArchiveEnterpriseBlockedModal from "./ArchiveEnterpriseBlockedModal"
import GeneralInfoSection from "./GeneralInfoSection"
import SitesLivraisonSection from "./distributionCenters/SitesLivraisonSection"
import ContactsSection from "./contacts/ContactsSection"
import LabelTemplatesSection from "./cardSettings/LabelTemplatesSection"
import RecipeCatalogSection from "./cardSettings/RecipeCatalogSection"
import EnterpriseHeaderMedia from "./EnterpriseHeaderMedia"
import PackagingSettingsSection from "./conditioning/PackagingSettingsSection"

const EnterpriseDetail = () => {
  const enterprise = useSelector(getOrganizationsEnterpriseSelector)
  const [archiveModalMode, setArchiveModalMode] = useState(null)
  const [activeTab, setActiveTab] = useState("general")
  const [archiveBlockedReason, setArchiveBlockedReason] = useState(null)

  if (!enterprise) {
    return (
      <Box sx={sx.page}>
        <EnterprisesHeader />
      </Box>
    )
  }

  const isArchived = enterprise.status === "ARCHIVED"

  const options = useMemo(() => {
    return [
        {
          label: "Statut",
          value: (
            <Typography sx={isArchived ? sx.statusArchived : sx.statusActive}>
              {isArchived ? "Archivé" : "Actif"}
            </Typography>
          ),
        },
        {
          label: "Date de création",
          value: dayjs(enterprise.createdAt).format(TableDateFormat),
        },
        {
          label: "Code",
          value: enterprise.code
        }
      ]
  }, [enterprise, isArchived])

  return (
    <Box sx={sx.page}>
      <EnterprisesHeader subtitle={enterprise.name} />

      <PreviewHeader
        title={enterprise.name}
        mediaSlot={<EnterpriseHeaderMedia enterprise={enterprise} />}
        options={options}
        rightActions={
          <IconButton
            sx={sx.actionBtn}
            onClick={() => setArchiveModalMode(isArchived ? "reactivate" : "archive")}
            aria-label={isArchived ? "Réactiver l'entreprise" : "Archiver l'entreprise"}
            title={isArchived ? "Réactiver l'entreprise" : "Archiver l'entreprise"}
          >
            {isArchived ? <UnarchiveIcon /> : <Inventory2Icon />}
          </IconButton>
        }
      />

      <Tabs value={activeTab} onChange={(_e, value) => setActiveTab(value)} sx={sx.tabs} centered>
        <Tab label="Général" value="general" />
        <Tab label="Paramétrage de la carte" value="cardSettings" />
      </Tabs>

      <Box sx={sx.content}>
        <Box sx={sx.contentInner}>
          {activeTab === "general" && (
            <>
              <GeneralInfoSection />
              <SitesLivraisonSection />
              <ContactsSection />
            </>
          )}
          {activeTab === "cardSettings" && (
            <>
              <RecipeCatalogSection />
              <PackagingSettingsSection />
              <LabelTemplatesSection />
            </>
          )}
        </Box>
      </Box>

      <ArchiveEnterpriseConfirmModal
        open={!!archiveModalMode}
        mode={archiveModalMode}
        onClose={() => setArchiveModalMode(null)}
        onBlocked={(reason) => setArchiveBlockedReason(reason)}
        enterpriseId={enterprise.objectId}
        enterpriseName={enterprise.name}
      />

      <ArchiveEnterpriseBlockedModal
        open={!!archiveBlockedReason}
        reason={archiveBlockedReason}
        onClose={() => setArchiveBlockedReason(null)}
      />
    </Box>
  )
}

const sx = {
  page: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
  },
  tabs: {
    px: 3,
    borderBottom: "1px solid",
    borderColor: "divider",
    minHeight: 48,
    "& .MuiTab-root": {
      minHeight: 48,
      textTransform: "uppercase",
      fontSize: 14,
      fontWeight: 500,
    },
  },
  content: {
    py: "40px",
    overflowY: "auto",
    flex: 1,
    bgcolor: COLORS.BACKGROUND_GREY,
  },
  contentInner: {
    width: "100%",
    maxWidth: 1140,
    mx: "auto",
    px: 3,
    display: "flex",
    flexDirection: "column",
    gap: 3,
  },
  actionBtn: {
    color: COLORS.WHITE,
    alignSelf: "flex-start",
    m: 2,
  },
  statusActive: {
    fontSize: "16px",
    marginTop: "8px",
    color: "#6BCD8A",
  },
  statusArchived: {
    fontSize: "16px",
    marginTop: "8px",
    color: "#B0BEC5",
  },
}

export default EnterpriseDetail
